// packages/types/src/uc/attributes.ts
// UC atribútové číselníky — hodnota ↔ UC ID ↔ SK label. Zdroj: docs counters (2026-08-21).

// Číselník bez doménových závislostí — `domain/property.types.ts` z neho ťahá
// Uc* typy (`import type`), naspäť sem neimportuje nič. Prevody, ktoré potrebujú
// naše doménové typy, žijú v `uc-export.ts` (napr. ENERGY_CLASS_TO_UC_CERT).

export interface UcTableDef { ucId: number; label: string }
const def = <T extends Record<string, UcTableDef>>(t: T) => t

export const UC_TRANSACTIONS = def({
  SALE:     { ucId: 127, label: 'Predaj' },
  RENT:     { ucId: 123, label: 'Prenájom' },        // Hireout = dám do prenájmu
  LEASE:    { ucId: 124, label: 'Podnájom' },
  EXCHANGE: { ucId: 125, label: 'Výmena' },
  AUCTION:  { ucId: 126, label: 'Dražba' },
  PURCHASE: { ucId: 122, label: 'Kúpa' },
})
export type UcTransaction = keyof typeof UC_TRANSACTIONS

export const UC_CONDITIONS = def({
  NEWBUILDING:            { ucId: 136, label: 'Novostavba' },
  PARTIAL_RECONSTRUCTION: { ucId: 137, label: 'Čiastočná rekonštrukcia' },
  COMPLETE_RECONSTRUCTION:{ ucId: 138, label: 'Kompletná rekonštrukcia' },
  ORIGINAL_CONDITION:     { ucId: 139, label: 'Pôvodný stav' },
  UNDER_CONSTRUCTION:     { ucId: 140, label: 'Vo výstavbe' },
  DEVELOP_PROJECT:        { ucId: 141, label: 'Developerský projekt' },
  INTENDED_TO_DEMOLITION: { ucId: 142, label: 'Určené na demoláciu' },
})
export type UcCondition = keyof typeof UC_CONDITIONS

export const UC_POSITIONS = def({
  SUPERSTRUCTURE:      { ucId: 143,   label: 'Nadstavba' },
  LOFT:                { ucId: 144,   label: 'Podkrovie' },
  GROUND_FLOOR:        { ucId: 368,   label: 'Prízemie' },
  HIGHER_GROUND_FLOOR: { ucId: 369,   label: 'Vyvýšené prízemie' },
  BASEMENT:            { ucId: 21503, label: 'Suterén' },
  TOP_FLOOR:           { ucId: 26103, label: 'Posledné poschodie' },
  HIGHER_FLOOR:        { ucId: 30826, label: 'Vyššie poschodie' },
})
export type UcPosition = keyof typeof UC_POSITIONS

export const UC_UNITS = def({
  TOTAL:        { ucId: 147, label: 'Celková' },
  PER_M2:       { ucId: 148, label: '€/m²' },
  PER_MONTH:    { ucId: 149, label: '€/mesiac' },
  PER_YEAR:     { ucId: 150, label: '€/rok' },
  PER_M2_MONTH: { ucId: 151, label: '€/m²/mesiac' },
  PER_M2_YEAR:  { ucId: 152, label: '€/m²/rok' },
  PER_DAY:      { ucId: 153, label: '€/deň' },
})
export type UcUnit = keyof typeof UC_UNITS

export const UC_CURRENCIES = def({
  EUR: { ucId: 167, label: 'EUR' }, CZK: { ucId: 168, label: 'CZK' },
  GBP: { ucId: 169, label: 'GBP' }, USD: { ucId: 170, label: 'USD' },
  PLN: { ucId: 171, label: 'PLN' }, HUF: { ucId: 172, label: 'HUF' },
  RON: { ucId: 173, label: 'RON' }, RSD: { ucId: 23345, label: 'RSD' },
})
export type UcCurrency = keyof typeof UC_CURRENCIES

export const UC_AVAILABILITY = def({
  ACTIVE:   { ucId: 30822, label: 'Aktívna' },
  RESERVED: { ucId: 242,   label: 'Rezervovaná' },
  SOLD:     { ucId: 241,   label: 'Predaná' },
  IN_RENT:  { ucId: 30824, label: 'Prenajatá' },
  CANCELED: { ucId: 30823, label: 'Zrušená' },
})
export type UcAvailability = keyof typeof UC_AVAILABILITY

export const UC_ENERGY_CERTS = def({
  A: { ucId: 305, label: 'A' }, B: { ucId: 306, label: 'B' }, C: { ucId: 307, label: 'C' },
  D: { ucId: 308, label: 'D' }, E: { ucId: 309, label: 'E' }, F: { ucId: 310, label: 'F' },
  G: { ucId: 20029, label: 'G' }, NONE: { ucId: 20030, label: 'Bez certifikátu' },
})
export type UcEnergyCert = keyof typeof UC_ENERGY_CERTS

// ENERGY_CLASS_TO_UC_CERT býval tu; žije v `uc-export.ts` (potrebuje doménový
// PropertyEnergyClass) a exportuje ho `index.ts` priamo odtiaľ.

export const UC_OWNERSHIPS = def({
  PERSONAL:    { ucId: 344, label: 'Osobné' },
  COOPERATIVE: { ucId: 340, label: 'Družstevné' },
  COMPANY:     { ucId: 341, label: 'Firemné' },
  STATE:       { ucId: 345, label: 'Štátne' },
  MUNICIPAL:   { ucId: 343, label: 'Obecné' },
  UNSETTLED:   { ucId: 342, label: 'Nevysporiadané' },
  OTHER:       { ucId: 346, label: 'Iné' },
})
export type UcOwnership = keyof typeof UC_OWNERSHIPS

export const UC_EQUIP_TYPES = def({
  FURNISHED:           { ucId: 531,   label: 'Zariadený' },
  PARTIALLY_FURNISHED: { ucId: 529,   label: 'Čiastočne zariadený' },
  UNFURNISHED:         { ucId: 530,   label: 'Nezariadený' },
  SHELL_SPACE:         { ucId: 20038, label: 'Holopriestor' },
})
export type UcEquipType = keyof typeof UC_EQUIP_TYPES

export const UC_ORIENTATIONS = def({
  NORTH: { ucId: 384, label: 'Sever' }, NORTHEAST: { ucId: 388, label: 'Severovýchod' },
  EAST: { ucId: 385, label: 'Východ' }, SOUTHEAST: { ucId: 386, label: 'Juhovýchod' },
  SOUTH: { ucId: 381, label: 'Juh' },   SOUTHWEST: { ucId: 383, label: 'Juhozápad' },
  WEST: { ucId: 382, label: 'Západ' },  NORTHWEST: { ucId: 387, label: 'Severozápad' },
})
export type UcOrientation = keyof typeof UC_ORIENTATIONS

export const UC_BUILDING_TYPES = def({
  BRICK: { ucId: 418, label: 'Tehla' }, PANEL: { ucId: 416, label: 'Panel' },
  WOOD: { ucId: 411, label: 'Drevo' }, STONE: { ucId: 413, label: 'Kameň' },
  EARTHEN: { ucId: 412, label: 'Hlina' }, BLOCK: { ucId: 414, label: 'Bloková konštrukcia' },
  PREFABRICATED: { ucId: 415, label: 'Montovaná' }, SKELETAL: { ucId: 417, label: 'Skelet' },
  REINFORCED_CONCRETE: { ucId: 420, label: 'Železobetón' }, METAL: { ucId: 421, label: 'Kov' },
  MIXED: { ucId: 419, label: 'Zmiešaná' }, OTHER: { ucId: 422, label: 'Iná' },
})
export type UcBuildingType = keyof typeof UC_BUILDING_TYPES

export const UC_GROUND_TYPES = def({
  FLATLAND: { ucId: 398, label: 'Rovina' }, MOSTLY_FLAT: { ucId: 399, label: 'Prevažne rovný' },
  SLIGHTLY_SLOPING: { ucId: 396, label: 'Mierne svahovitý' }, SLOPING: { ucId: 397, label: 'Svahovitý' },
  STEEP_SLOPE: { ucId: 401, label: 'Strmý svah' }, TERRACED: { ucId: 400, label: 'Terasovitý' },
  OTHER: { ucId: 402, label: 'Iný' },
})
export type UcGroundType = keyof typeof UC_GROUND_TYPES

export const UC_HEATING_SYSTEMS = def({
  CENTRAL: { ucId: 717, label: 'Centrálne' }, LOCAL: { ucId: 723, label: 'Lokálne' },
  UNDERFLOOR: { ucId: 722, label: 'Podlahové' }, FIREPLACE: { ucId: 729, label: 'Kozub' },
  AIR_CONDITION: { ucId: 730, label: 'Klimatizácia' }, EMITTERS: { ucId: 732, label: 'Radiátory' },
  NOT_AVAILABLE: { ucId: 30739, label: 'Bez kúrenia' }, OTHER: { ucId: 728, label: 'Iné' },
})
export type UcHeatingSystem = keyof typeof UC_HEATING_SYSTEMS

export const UC_TERRITORIES = def({
  INTRAVILAN: { ucId: 825, label: 'Intravilán' },
  EXTRAVILAN: { ucId: 826, label: 'Extravilán' },
})
export type UcTerritory = keyof typeof UC_TERRITORIES

export const UC_READY_TO_BUILD = def({
  PRELIMINARY_AGREEMENT: { ucId: 851, label: 'Predbežný súhlas' },
  APPROVED_IN_PLAN:      { ucId: 852, label: 'Schválené v územnom pláne' },
  ZONING_PERMIT:         { ucId: 853, label: 'Vydané územné rozhodnutie' },
  BUILDING_PERMIT:       { ucId: 854, label: 'Vydané stavebné povolenie' },
})
export type UcReadyToBuild = keyof typeof UC_READY_TO_BUILD

export const UC_HOUSE_TYPES = def({
  CLASSIC: { ucId: 975, label: 'Klasický dom' }, FAMILY_VILLA: { ucId: 976, label: 'Rodinná vila' },
  BUNGALOW: { ucId: 977, label: 'Bungalov' }, WOODEN: { ucId: 978, label: 'Drevodom' },
  LOG_CABIN: { ucId: 979, label: 'Zrub' }, DOUBLE_GENERATIONAL: { ucId: 980, label: 'Dvojgeneračný' },
})
export type UcHouseType = keyof typeof UC_HOUSE_TYPES

export const UC_PARKING_TYPES = def({
  DETACHED_GARAGE:        { ucId: 30748, label: 'Samostatná garáž' },
  GARAGE_IN_HOUSE:        { ucId: 30749, label: 'Garáž v parkovacom dome' },
  SPACE_IN_PARKING_HOUSE: { ucId: 30750, label: 'Miesto v parkovacom dome' },
  OUTDOOR_SPACE:          { ucId: 30751, label: 'Vonkajšie parkovacie miesto' },
  OTHER:                  { ucId: 30753, label: 'Iné' },
})
export type UcParkingType = keyof typeof UC_PARKING_TYPES

export const UC_WATER = def({
  ON_LAND: { ucId: 691, label: 'Na pozemku' }, NEAR_LAND: { ucId: 692, label: 'Blízko pozemku' },
  ON_LAND_SUPPLY: { ucId: 693, label: 'Na pozemku — vodovod' }, ON_LAND_WELL: { ucId: 694, label: 'Na pozemku — studňa' },
  NEAR_LAND_SUPPLY: { ucId: 695, label: 'Blízko — vodovod' }, NEAR_LAND_WELL: { ucId: 696, label: 'Blízko — studňa' },
  NOT_AVAILABLE: { ucId: 24704, label: 'Nedostupná' },
})
export type UcWater = keyof typeof UC_WATER

export const UC_ELECTRIC = def({
  ON_LAND: { ucId: 698, label: 'Na pozemku' }, NEAR_LAND: { ucId: 699, label: 'Blízko pozemku' },
  ON_LAND_230V: { ucId: 700, label: 'Na pozemku — 230 V' }, ON_LAND_380V: { ucId: 701, label: 'Na pozemku — 380 V' },
  NEAR_LAND_230V: { ucId: 702, label: 'Blízko — 230 V' }, NEAR_LAND_380V: { ucId: 703, label: 'Blízko — 380 V' },
  NOT_AVAILABLE: { ucId: 24707, label: 'Nedostupná' },
})
export type UcElectric = keyof typeof UC_ELECTRIC

export const UC_GAS = def({
  ON_LAND: { ucId: 705, label: 'Na pozemku' }, NEAR_LAND: { ucId: 706, label: 'Blízko pozemku' },
  NOT_AVAILABLE: { ucId: 24709, label: 'Nedostupný' },
})
export type UcGas = keyof typeof UC_GAS

export const UC_SEWAGE = def({
  ON_LAND_SEWER: { ucId: 710, label: 'Na pozemku — kanalizácia' }, ON_LAND_SEPTIC: { ucId: 711, label: 'Na pozemku — septik' },
  ON_LAND_CESSPOOL: { ucId: 712, label: 'Na pozemku — žumpa' }, ON_LAND_TREATMENT: { ucId: 708, label: 'Na pozemku — ČOV' },
  NEAR_LAND_SEWER: { ucId: 713, label: 'Blízko — kanalizácia' }, NEAR_LAND_SEPTIC: { ucId: 714, label: 'Blízko — septik' },
  NEAR_LAND_TREATMENT: { ucId: 709, label: 'Blízko — ČOV' }, NOT_AVAILABLE: { ucId: 24711, label: 'Nedostupná' },
})
export type UcSewage = keyof typeof UC_SEWAGE

export const UC_COMMISSION_UNITS = def({
  PERCENT: { ucId: 748, label: '%' },
  CURRENCY: { ucId: 749, label: 'Mena' },
})
export type UcCommissionUnit = keyof typeof UC_COMMISSION_UNITS

/** Portály UC siete pre notexport_portals (výber; kompletný zoznam v ExportSetting.config). */
export const UC_PORTALS = def({
  NEHNUTELNOSTI_SK: { ucId: 30821, label: 'Nehnutelnosti.sk' },
  TOPREALITY_SK:    { ucId: 30818, label: 'Topreality.sk' },
  REALITY_SK:       { ucId: 30817, label: 'Reality.sk' },
  BAZOS_SK:         { ucId: 30797, label: 'Bazos.sk' },
  SREALITY_CZ:      { ucId: 30819, label: 'Sreality.cz' },
})
export type UcPortal = keyof typeof UC_PORTALS

/**
 * Uzavretý enum `Advertisement.notexport_portals.items` (35 ID) z UC Import API.
 * Zdroj: `docs/integrations/uc-openapi.json` — zhodu s ním stráži contract test
 * vo feed builderi, ktorý ten JSON číta za behu.
 *
 * Nadmnožina `UC_PORTALS` (tá drží len portály, ktoré vieme pomenovať). ID mimo
 * tejto sady zhodí CELÝ import na 422, nie len jeden inzerát — preto sa hodnoty
 * z konfigu filtrujú cez `filterNotexportPortals`.
 */
export const UC_NOTEXPORT_PORTAL_IDS: readonly number[] = [
  30787, 30788, 30789, 30790, 30791, 30792, 30793, 30794, 30795, 30796, 30797, 30798,
  30799, 30800, 30801, 30802, 30803, 30804, 30805, 30806, 30807, 30808, 30809, 30810,
  30811, 30812, 30813, 30814, 30815, 30816, 30817, 30818, 30819, 30820, 30821,
]
const NOTEXPORT_PORTAL_ID_SET = new Set(UC_NOTEXPORT_PORTAL_IDS)

/** Ponechá len ID z uzavretého enumu — neznáme ticho vypadnú. */
export const filterNotexportPortals = (ids: number[]): number[] =>
  ids.filter((id) => NOTEXPORT_PORTAL_ID_SET.has(id))

export function ucId<T extends Record<string, UcTableDef>>(table: T, key: keyof T & string): number {
  return (table[key] as UcTableDef).ucId
}

/** Bezpečný lookup pre runtime stringy (DB hodnoty) — neznámy kľúč vráti undefined, nie crash. */
export function ucIdOf<T extends Record<string, UcTableDef>>(
  table: T, key: string | null | undefined,
): number | undefined {
  if (key == null) return undefined
  return (table as Record<string, UcTableDef | undefined>)[key]?.ucId
}

/** Odvodí { kľúč: label } mapu z UC tabuľky — jediný zdroj pravdy pre labels.ts. */
export function ucLabels(table: Record<string, UcTableDef>): Record<string, string> {
  return Object.fromEntries(Object.entries(table).map(([k, v]) => [k, v.label]))
}

/** Odvodí select options ({ value, label }[]) z UC tabuľky — value je zúžený na kľúče T. */
export function ucOptions<T extends Record<string, UcTableDef>>(
  table: T,
): Array<{ value: keyof T & string; label: string }> {
  return Object.entries(table).map(([value, v]) => ({ value: value as keyof T & string, label: v.label }))
}
